import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { cancelDiplome } from "../../api/diplome";
import { useEffect } from "react";

type Props = {
  show: boolean;
  diplome: any;
  onClose: () => void;
  onSaved: () => void;
};

const CancelDiplome = ({ show, diplome, onClose, onSaved }: Props) => {
  const createSchema = z.object({
    id: z.string().nonempty("ID requis"),
    reason_for_cancellation: z.string().nonempty("Raison d'annulation requise"),
    observation_cancellation: z.string().optional(),
    confirm_irreversible: z.boolean().refine((value) => value === true, {
      message: "Vous devez confirmer que l'annulation est irréversible.",
    }),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un type de niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await cancelDiplome(
        diplome?.id,
        data,
        buildPermissionContext(Resource.DIPLOME, Action.DELETE),
      );

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      onClose(); // On ferme le modal
      toast.success("Diplôme annulé !"); //Toast de succès
    } catch (error: any) {
      // console.log(error)
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (diplome) {
      reset({
        id: diplome.id,
        confirm_irreversible: false,
      });
    }
  }, []);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Annuler le diplôme {diplome?.number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Raison d'annulation
                  </label>
                  <select
                    {...register("reason_for_cancellation")}
                    className={
                      errors.reason_for_cancellation
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value="">Sélectionnez une raison</option>
                    <option value="Perte ou Vol">Perte ou Vol</option>
                    <option value="Erreur de Note">Erreur de Note</option>
                    <option value="Information Personnelle Erronée">
                      Information Personnelle Erronée
                    </option>
                    <option value="Fraude ou Plagiat">Fraude ou Plagiat</option>
                    <option value="Autre">
                      Autre (préciser dans les observations)
                    </option>
                  </select>
                  {errors.reason_for_cancellation && (
                    <div className="invalid-feedback">
                      {errors.reason_for_cancellation.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Observation d'annulation
                  </label>
                  <textarea
                    {...register("observation_cancellation")}
                    className={
                      errors.observation_cancellation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Observation d'annulation"
                  />
                  {errors.observation_cancellation && (
                    <div className="invalid-feedback">
                      {errors.observation_cancellation.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div
                  className={`alert alert-danger mb-3 ${errors.confirm_irreversible ? "border border-danger" : ""}`}
                >
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="confirm_irreversible"
                      className={`form-check-input ${errors.confirm_irreversible ? "is-invalid" : ""}`}
                      {...register("confirm_irreversible")}
                    />
                    <label
                      className="form-check-label fw-semibold"
                      htmlFor="confirm_irreversible"
                    >
                      Je confirme que cette action d'annulation est
                      irréversible.
                    </label>
                    <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                      Une fois annulé, ce diplôme ne sera plus valable et ne
                      pourra plus servir de document officiel à qui de droit.
                    </div>
                    {errors.confirm_irreversible && (
                      <div className="invalid-feedback d-block">
                        {errors.confirm_irreversible.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CancelDiplome,
  [buildPermission(Resource.DIPLOME, Action.DELETE)],
  "masquer",
);
